/**
 * Collection Template
 * - Keeps product card variant and button state in sync
 * - Updates card images when variants are selected
 * - Ensures correct variant is added to cart
 */

function redBadgeLabelFromVariant(variantData, fallback) {
  const fb = fallback != null && fallback !== '' ? String(fallback) : ''
  if (!variantData) return fb
  const price = Number(variantData.price)
  const compare = Number(variantData.compareAtPrice || 0)
  if (!compare || compare <= price || !Number.isFinite(price)) return fb
  const pct = Math.round((compare - price) * 100 / compare)
  if (pct <= 0) return fb
  return `${pct}% OFF`
}

function syncProductCardRedBadge(card, variantData) {
  const badgeEl = card.querySelector('[data-product-card-red-badge]')
  if (!badgeEl) return
  const fallback = badgeEl.dataset.redBadgeFallback || ''
  const label = redBadgeLabelFromVariant(variantData, fallback)
  badgeEl.textContent = label
  badgeEl.classList.toggle('hidden', !label)
}

function initCollectionTemplate() {
  const productForms = document.querySelectorAll('[data-product-form]')

  productForms.forEach((form) => {
    const variantSelect = form.querySelector('[data-variant-select]')
    const variantInput = form.querySelector('[data-variant-input]')
    const submitButton = form.querySelector('[data-submit-button]')
    const buttonText = form.querySelector('[data-button-text]')

    if (!variantSelect || !variantInput) return

    // Sync state function
    const syncState = () => {
      const selectedOption = variantSelect.options[variantSelect.selectedIndex]
      if (selectedOption && variantInput) {
        variantInput.value = selectedOption.value
      }

      const isAvailable = !(selectedOption && (selectedOption.disabled || selectedOption.getAttribute('data-variant-available') === 'false'))
      if (submitButton) {
        submitButton.disabled = !isAvailable
      }
      if (buttonText) {
        const customText = form.dataset.customButtonText?.trim()
        buttonText.textContent = isAvailable ? (customText || 'Add to Cart') : 'Sold Out'
      }
    }

    variantSelect.addEventListener('change', syncState)
    // Initial sync
    syncState()

    // Handle swatch clicks to update card image and variant
    const swatches = form.querySelectorAll('.variant-swatch')
    swatches.forEach((swatch) => {
      swatch.addEventListener('click', (e) => {
        // Don't handle if disabled
        if (swatch.disabled) return

        const variantId = parseInt(swatch.dataset.variantId)
        if (isNaN(variantId)) return

        // Update variant select (for VariantManager compatibility) and hidden input (for form submission)
        variantSelect.value = variantId
        
        // Ensure the option is actually selected
        const optionToSelect = Array.from(variantSelect.options).find(opt => parseInt(opt.value) === variantId)
        if (optionToSelect) {
          optionToSelect.selected = true
        }
        
        // Update the hidden input - this is what FormData will read for form submission
        variantInput.value = variantId
        variantInput.setAttribute('value', variantId) // Ensure attribute is also updated
        
        // Trigger change event on select to ensure all listeners are notified
        variantSelect.dispatchEvent(new Event('change', { bubbles: true }))
        
        syncState()

        // Update swatch active states
        swatches.forEach((s) => {
          s.classList.remove('active')
          s.setAttribute('aria-pressed', 'false')
        })
        swatch.classList.add('active')
        swatch.setAttribute('aria-pressed', 'true')

        // Update card image
        const productCard = form.closest('[data-product-card]')
        if (productCard) {
          const productJsonScript = productCard.querySelector('[data-product-card-json]')
          if (productJsonScript) {
            try {
              const productData = JSON.parse(productJsonScript.textContent)
              const variantData = productData.variants.find(v => v.id === variantId)

              if (variantData) {
                // Update image if available
                if (variantData.image) {
                  const cardImage = productCard.querySelector('[data-card-image]')
                  if (cardImage) {
                    // Update image with fade effect
                    cardImage.style.transition = 'opacity 0.3s ease-in-out'
                    cardImage.style.opacity = '0'
                    
                    setTimeout(() => {
                      cardImage.src = variantData.image
                      cardImage.srcset = `${variantData.image} 1x, ${variantData.image.replace('width=600', 'width=1200')} 2x`
                      cardImage.style.opacity = '1'
                    }, 150)
                  }
                }

                // Update price
                const priceElement = productCard.querySelector('[data-product-card-price]')
                const comparePriceElement = productCard.querySelector('[data-product-card-compare-price]')
                
                if (priceElement && variantData.priceFormatted) {
                  priceElement.innerHTML = variantData.priceFormatted
                }
                
                if (comparePriceElement) {
                  if (variantData.compareAtPriceFormatted) {
                    comparePriceElement.innerHTML = variantData.compareAtPriceFormatted
                    comparePriceElement.classList.remove('hidden')
                  } else {
                    comparePriceElement.classList.add('hidden')
                  }
                }

                syncProductCardRedBadge(productCard, variantData)
              }
            } catch (error) {
              console.warn('[Collection] Failed to update card image:', error)
            }
          }
        }
      })
    })
  })

  // Handle product-card swatch clicks (for style-2 cards)
  const productCards = document.querySelectorAll('[data-product-card]')
  productCards.forEach((card) => {
    const cardSwatches = card.querySelectorAll('[data-product-card-swatch]')
    const productForm = card.querySelector('[data-product-form]')
    
    // Check if this is a separated variant card
    const isSeparatedVariantCard = card.dataset.separatedVariant === 'true'
    // Check if this has custom swatches from metaobject
    const hasCustomSwatches = card.querySelector('[data-custom-swatches="true"]') !== null
    
    // Only show checkmarks if not separated variant card and not custom swatches
    const shouldShowCheckmark = !isSeparatedVariantCard && !hasCustomSwatches && cardSwatches.length > 1
    
    // Set initial checkmark state based on aria-pressed
    cardSwatches.forEach((swatch) => {
      if (swatch.getAttribute('aria-pressed') === 'true' && shouldShowCheckmark) {
        const checkmarkSvg = '<svg width="13" height="10" viewBox="0 0 13 10" fill="none" xmlns="http://www.w3.org/2000/svg" class="product-card__swatch-checkmark"><path d="M4.54601 10L0 5.25988L1.1365 4.07484L4.54601 7.62994L11.8635 0L13 1.18503L4.54601 10Z" fill="white"/></svg>'
        const isWrapper = swatch.classList.contains('product-card__swatch-wrapper')
        if (!swatch.querySelector('.product-card__swatch-checkmark')) {
          if (isWrapper) {
            swatch.insertAdjacentHTML('beforeend', checkmarkSvg.replace('class="product-card__swatch-checkmark"', 'class="product-card__swatch-checkmark absolute inset-0 m-auto z-10"'))
          } else {
            swatch.insertAdjacentHTML('beforeend', checkmarkSvg)
          }
        }
      }
    })
    
    cardSwatches.forEach((swatch) => {
      swatch.addEventListener('click', (e) => {
        // Don't handle if disabled
        if (swatch.disabled) return

        const variantId = parseInt(swatch.dataset.variantId)
        if (isNaN(variantId)) return

        // Update card image (only for regular variants, not custom swatches)
        const productJsonScript = card.querySelector('[data-product-card-json]')
        if (productJsonScript) {
          try {
            const productData = JSON.parse(productJsonScript.textContent)
            // Only update image if it's a regular variant (not a custom swatch)
            const variantData = productData.variants.find(v => v.id === variantId)

            if (variantData) {
              // Update image if available
              if (variantData.image) {
                const cardImage = card.querySelector('[data-card-image]')
                if (cardImage) {
                  // Update image with fade effect
                  cardImage.style.transition = 'opacity 0.3s ease-in-out'
                  cardImage.style.opacity = '0'
                  
                  setTimeout(() => {
                    cardImage.src = variantData.image
                    cardImage.srcset = `${variantData.image} 1x, ${variantData.image.replace('width=600', 'width=1200')} 2x`
                    cardImage.style.opacity = '1'
                  }, 150)
                }
              }

              // Update price
              const priceElement = card.querySelector('[data-product-card-price]')
              const comparePriceElement = card.querySelector('[data-product-card-compare-price]')
              
              if (priceElement && variantData.priceFormatted) {
                priceElement.innerHTML = variantData.priceFormatted
              }
              
              if (comparePriceElement) {
                if (variantData.compareAtPriceFormatted) {
                  comparePriceElement.innerHTML = variantData.compareAtPriceFormatted
                  comparePriceElement.classList.remove('hidden')
                } else {
                  comparePriceElement.classList.add('hidden')
                }
              }

              syncProductCardRedBadge(card, variantData)
            }
            // Note: Custom swatches don't update the product card image, only the swatch itself
          } catch (error) {
            console.warn('[Collection] Failed to update card image:', error)
          }
        }

        // Update form if it exists
        if (productForm) {
          const variantSelect = productForm.querySelector('[data-variant-select]')
          const variantInput = productForm.querySelector('[data-variant-input]')
          const variantInputId = productForm.querySelector('[data-variant-id]') || variantInput
          
          if (variantSelect) {
            variantSelect.value = variantId
            const optionToSelect = Array.from(variantSelect.options).find(opt => parseInt(opt.value) === variantId)
            if (optionToSelect) {
              optionToSelect.selected = true
            }
            variantSelect.dispatchEvent(new Event('change', { bubbles: true }))
          }
          
          if (variantInput) {
            variantInput.value = variantId
            variantInput.setAttribute('value', variantId)
            variantInput.setAttribute('name', 'id')
          }
          
          if (variantInputId) {
            variantInputId.value = variantId
            variantInputId.setAttribute('value', variantId)
          }

          // Dispatch variant:change event for VariantManager compatibility
          const variantChangeEvent = new CustomEvent('variant:change', {
            bubbles: true,
            detail: { variantId: variantId }
          })
          productForm.dispatchEvent(variantChangeEvent)
        }

        // Update swatch active states and checkmark
        const checkmarkSvg = '<svg width="13" height="10" viewBox="0 0 13 10" fill="none" xmlns="http://www.w3.org/2000/svg" class="product-card__swatch-checkmark"><path d="M4.54601 10L0 5.25988L1.1365 4.07484L4.54601 7.62994L11.8635 0L13 1.18503L4.54601 10Z" fill="white"/></svg>'
        
        cardSwatches.forEach((s) => {
          s.setAttribute('aria-pressed', 'false')
          // Remove checkmark from all swatches
          const existingCheckmark = s.querySelector('.product-card__swatch-checkmark')
          if (existingCheckmark) {
            existingCheckmark.remove()
          }
        })
        
        // Add checkmark to selected swatch only if should show checkmark
        swatch.setAttribute('aria-pressed', 'true')
        if (shouldShowCheckmark && !swatch.querySelector('.product-card__swatch-checkmark')) {
          // Check if it's a wrapper (has swatch snippet) or direct button
          const isWrapper = swatch.classList.contains('product-card__swatch-wrapper')
          if (isWrapper) {
            swatch.insertAdjacentHTML('beforeend', checkmarkSvg.replace('class="product-card__swatch-checkmark"', 'class="product-card__swatch-checkmark absolute inset-0 m-auto z-10"'))
          } else {
            swatch.insertAdjacentHTML('beforeend', checkmarkSvg)
          }
        }
      })
    })
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCollectionTemplate)
} else {
  initCollectionTemplate()
}

export default initCollectionTemplate


